import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Copy, KeyRound, Trash2 } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PortalApiKey {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string | null;
  keyPrefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

interface ListResponse {
  keys: PortalApiKey[];
  allScopes: string[];
}

interface CreateResponse {
  key: PortalApiKey;
  plaintext: string;
}

interface PortalContact {
  id: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

export function PortalApiKeysPage(): React.ReactElement {
  useDocumentTitle('API keys');
  const navigate = useNavigate();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [keys, setKeys] = useState<PortalApiKey[] | null>(null);
  const [allScopes, setAllScopes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['time-data:read']);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  async function loadAuth() {
    try {
      const r = await apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me');
      if (!r.contact) {
        navigate('/login', { replace: true, state: { from: '/portal/api-keys' } });
        return;
      }
      setContact(r.contact);
    } catch {
      navigate('/login', { replace: true, state: { from: '/portal/api-keys' } });
    } finally {
      setAuthChecked(true);
    }
  }

  async function load() {
    try {
      const r = await apiFetch<ListResponse>('/api/portal/api-keys');
      setKeys(r.keys);
      setAllScopes(r.allScopes);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load API keys.');
    }
  }

  useEffect(() => {
    void loadAuth();
  }, []);

  useEffect(() => {
    if (contact?.isOrgAdmin) void load();
  }, [contact]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || selectedScopes.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const r = await apiFetch<CreateResponse>('/api/portal/api-keys', {
        method: 'POST',
        json: { name: name.trim(), scopes: selectedScopes },
      });
      setJustCreated(r);
      setName('');
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create key.');
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id: string, label: string) {
    if (!window.confirm(`Revoke "${label}"? Any integration using this key will start failing immediately.`)) return;
    try {
      await apiFetch(`/api/portal/api-keys/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to revoke key.');
    }
  }

  function toggleScope(s: string) {
    setSelectedScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function copyPlaintext(plaintext: string) {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable; the input is selectable as fallback.
    }
  }

  function shell(children: React.ReactNode): React.ReactElement {
    return (
      <div className="min-h-screen bg-[var(--color-surface)]">
        <PortalNavBar contact={contact} />
        <PortalHealthBanner />
        <main className="mx-auto max-w-5xl space-y-6 px-6 py-8">{children}</main>
        <PortalFooter />
      </div>
    );
  }

  if (!authChecked) {
    return shell(<p className="text-[var(--color-muted)]">Loading…</p>);
  }
  if (!contact) {
    return shell(<p className="text-[var(--color-muted)]">Loading…</p>);
  }
  if (!contact.isOrgAdmin) {
    return shell(
      <Card>
        <h1 className="text-2xl font-bold text-[var(--color-header)]">API keys</h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          Only your organization's admin contact can manage API keys. If you need access, ask your
          internal admin to add one for your integration.
        </p>
      </Card>,
    );
  }

  return shell(
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">API keys</h1>
          <p className="mt-1 max-w-2xl text-[var(--color-muted)]">
            Generate Bearer tokens to pull your organization's time and billing data into BI tools,
            spreadsheets, or your own apps. Each key only ever reads <strong>your</strong>{' '}
            organization's data — no other clients are visible. Browse the spec at{' '}
            <a href="/api/v1/docs" target="_blank" rel="noreferrer" className="text-[var(--color-t3-blue)] hover:underline">
              /api/v1/docs
            </a>{' '}
            (Swagger UI).
          </p>
        </div>
        <Button onClick={() => setShowCreate((v) => !v)}>
          <KeyRound className="h-4 w-4" /> {showCreate ? 'Cancel' : 'New API key'}
        </Button>
      </div>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      {justCreated && (
        <Card className="border-[var(--color-t3-blue)]/40 bg-[var(--color-t3-blue)]/5">
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            Key created — copy it now
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            This is the only time the full key will be shown. After you leave this page you'll only
            see the prefix.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <input
              readOnly
              value={justCreated.plaintext}
              onClick={(e) => (e.target as HTMLInputElement).select()}
              className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 font-mono text-xs"
            />
            <Button onClick={() => void copyPlaintext(justCreated.plaintext)}>
              <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <div className="mt-3 text-xs text-[var(--color-muted)]">
            Use it like:{' '}
            <code className="rounded bg-white px-1 py-0.5">
              curl -H "Authorization: Bearer {justCreated.plaintext.slice(0, 12)}…" {window.location.origin}/api/v1/time-entries
            </code>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setJustCreated(null)}
              className="text-xs text-[var(--color-muted)] hover:underline"
            >
              I've copied it — dismiss
            </button>
          </div>
        </Card>
      )}

      {showCreate && (
        <Card>
          <form onSubmit={create} className="space-y-4">
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Name</span>
              <input
                type="text"
                required
                maxLength={100}
                placeholder="e.g. Power BI dashboard"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                Just for your reference — what's this key for?
              </span>
            </label>
            <div>
              <span className="block text-xs font-medium text-[var(--color-header)]">Scopes</span>
              <div className="mt-2 space-y-2">
                {allScopes.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedScopes.includes(s)}
                      onChange={() => toggleScope(s)}
                      className="h-4 w-4"
                    />
                    <code className="rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-xs">{s}</code>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 border-t border-[var(--color-border)] pt-3">
              <Button type="submit" disabled={creating || !name.trim() || selectedScopes.length === 0}>
                {creating ? 'Creating…' : 'Create key'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden !p-0">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Prefix</th>
              <th className="px-4 py-3">Scopes</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Last used</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {keys === null ? (
              <tr>
                <td className="px-4 py-6 text-[var(--color-muted)]" colSpan={7}>
                  Loading…
                </td>
              </tr>
            ) : keys.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-[var(--color-muted)]" colSpan={7}>
                  No API keys yet.
                </td>
              </tr>
            ) : (
              keys.map((k) => (
                <tr key={k.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3 font-medium text-[var(--color-header)]">{k.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{k.keyPrefix}…</td>
                  <td className="px-4 py-3">
                    {k.scopes.map((s) => (
                      <code key={s} className="mr-1 rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-xs">
                        {s}
                      </code>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {new Date(k.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {k.revokedAt ? (
                      <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-muted)]">
                        revoked
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!k.revokedAt && (
                      <button
                        type="button"
                        onClick={() => void revoke(k.id, k.name)}
                        className="inline-flex items-center gap-1 text-xs text-[var(--color-danger)] hover:underline"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </>,
  );
}
